import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { IntlProvider } from 'react-intl';
import { getAntLocale, getMessages, Locales } from 'src/i18n/index';
import { Header, Footer, AdminHeader, Markdown } from 'src/components/components/index';
import {
  Routes,
  ADMIN_PAGE,
  REQUEST_CANCEL_RESERVATION,
  RESERVATION_CANCELLATION,
} from 'src/constants/index';
import { UserAccountEnum, MarkDownEnum } from 'src/common-types/index';
import { Responsive } from 'src/utils/index';
import { HotelDetailPage } from '../hotel-details';
import { ErrorPage } from '../error';
import { GetawayPage } from '../getaway';
import { GetawayListPage } from '../getaway-list';
import { CondoDetailPage } from '../condo-details';
import { SubmitRequestPage } from '../all-inclusives/submit-request';
import { GetawayDetailsPage } from '../getaway-details';
import { CondoReviewBookPage } from '../condo-review-book';
import { ReservationCancellation } from '../reservation-cancellation';
import { RequestCancelReservation } from '../request-cancel-reservation';
import { HomePage } from '../home-page';
import { HomesPage } from '../homes';
import { Condominiums } from '../../components/condo/condominiums';
import { CondoSpecialsPage } from '../../components/condo/condo-specials';
import { GetawayBookingPage } from '../getaway-booking';
import { AmericanLifestylePage } from '../american-lifestyle';
import { ConciergeTravelTeamPage } from '../concierge';
import { ResourcesPage } from '../resources';
import { CruisesPage } from '../cruises';
import { DailyBenefitsPage } from '../daily-benefits';
import { LuxuryCruisesPage } from '../luxury-cruises';
import { ContactUsPage } from '../contact-us';
import { MasterClassesRegisterPage } from '../master-classes-register';
import { PersonalInfoPage } from '../personal-info';
import { PromotionDetailsPage } from '../promotion-details';
import './style.scss';

interface IProps {}

const ONE = 1;
const CondoPage = React.lazy(() => import('../condo/component'));
const ReviewBookPage = React.lazy(() => import('../review-book/component'));
const AdminPage = React.lazy(() => import('../admin-page/component'));
const SearchPage = React.lazy(() => import('../search/component'));
const MemberStoryPage = React.lazy(() => import('../member-story/component'));
const MemberVideosPage = React.lazy(() => import('../member-videos/component'));
const AllInclusivesPage = React.lazy(() => import('../all-inclusives/component'));
const GetawayCondoPage = React.lazy(() => import('../getaway-condo/component'));

export class App extends React.Component<IProps, null> {
  render(): React.ReactNode {
    const isAdminPage = location.pathname.split('/')[ONE] === ADMIN_PAGE;
    const isReservationCancellation =
      location.pathname.split('/')[ONE] === REQUEST_CANCEL_RESERVATION ||
      location.pathname.split('/')[ONE] === RESERVATION_CANCELLATION;

    return (
      <ConfigProvider locale={getAntLocale(Locales.English)}>
        <IntlProvider
          messages={getMessages(Locales.English)}
          locale={Locales.English}
          defaultLocale={Locales.English}
        >
          <div className={`app ${isReservationCancellation ? 'cancellation' : ''}`}>
            {isAdminPage ? (
              <AdminHeader />
            ) : (
              <Responsive>
                <Header />
              </Responsive>
            )}
            <React.Suspense fallback={<div />}>
              <Switch>
                <Route path={Routes.Home} exact={true}>
                  <HomePage />
                </Route>
                <Route path={Routes.Search} exact={true}>
                  <SearchPage />
                </Route>
                <Route path={`${Routes.Hotel}/:id`}>
                  <HotelDetailPage />
                </Route>
                <Route path={`${Routes.AdminHotel}/:id`}>
                  <HotelDetailPage isFromAdminPage />
                </Route>
                <Route path={`${Routes.Condo}`} exact={true}>
                  <Condominiums />
                </Route>
                <Route path={`${Routes.Condo}/specials`}>
                  <CondoSpecialsPage />
                </Route>
                <Route path={`${Routes.CondoSearch}`} exact>
                  <CondoPage />
                </Route>
                <Route path={`${Routes.CondoSearch}/submit-request`}>
                  <SubmitRequestPage isCondoPage />
                </Route>
                <Route path={`${Routes.Homes}`}>
                  <HomesPage />
                </Route>
                <Route path={`${Routes.Condo}/:id`}>
                  <CondoDetailPage isFromAdminPage={false} />
                </Route>
                <Route path={`${Routes.Cruises}`} exact={true}>
                  <CruisesPage />
                </Route>
                <Route path={`${Routes.Cruises}/luxury`}>
                  <LuxuryCruisesPage />
                </Route>
                <Route path={`${Routes.AdminCondo}/:id`}>
                  <CondoDetailPage isFromAdminPage={true} />
                </Route>
                <Route path={`${Routes.Getaway}`} exact={true}>
                  <GetawayPage />
                </Route>
                <Route path={`${Routes.GetawayDetails}/:id`}>
                  <GetawayDetailsPage />
                </Route>
                <Route path={`${Routes.GetawayCondo}`} exact={true}>
                  <GetawayCondoPage />
                </Route>
                <Route path={`${Routes.GetawayCondo}/:id`}>
                  <GetawayListPage />
                </Route>
                <Route path={`${Routes.Getaway}/:id`}>
                  <GetawayListPage />
                </Route>
                <Route path={`${Routes.GetawayBookingPage}`}>
                  <GetawayBookingPage />
                </Route>
                <Route path={`${Routes.Book}/:id`}>
                  <ReviewBookPage />
                </Route>
                <Route path={`${Routes.AllInclusive}`} exact>
                  <AllInclusivesPage />
                </Route>
                <Route path={`${Routes.AllInclusive}/submit-request`}>
                  <SubmitRequestPage />
                </Route>
                <Route path={`${Routes.UnitBook}/:id`}>
                  <CondoReviewBookPage />
                </Route>
                <Route path={`${Routes.AdminPage}`}>
                  <AdminPage />
                </Route>
                <Route path={`${Routes.ReservationCancellation}`}>
                  <ReservationCancellation />
                </Route>
                <Route path={`${Routes.RequestCancelReservation}`}>
                  <RequestCancelReservation />
                </Route>
                <Route path={`${Routes.AmericanLifestyle}`}>
                  <AmericanLifestylePage />
                </Route>
                <Route path={Routes.ConciergeTeam}>
                  <ConciergeTravelTeamPage />
                </Route>
                <Route path={Routes.Resources}>
                  <ResourcesPage />
                </Route>
                <Route path={Routes.Resources}>
                  <ResourcesPage />
                </Route>
                <Route path={Routes.DailyBenefits}>
                  <DailyBenefitsPage />
                </Route>
                <Route path={Routes.ContactUs}>
                  <ContactUsPage />
                </Route>
                <Route path={Routes.MasterClassesRegister}>
                  <MasterClassesRegisterPage />
                </Route>
                <Route path={Routes.Error} exact={true}>
                  <ErrorPage />
                </Route>
                <Route path={`${Routes.MemberStories}/:id`} exact={true}>
                  <MemberStoryPage />
                </Route>
                <Route path={Routes.MemberVideos} exact={true}>
                  <MemberVideosPage />
                </Route>
                <Route path={Routes.PersonalInfo} exact={true}>
                  <PersonalInfoPage activeKey={UserAccountEnum.PersonalInfo} />
                </Route>
                <Route path={Routes.Security} exact={true}>
                  <PersonalInfoPage activeKey={UserAccountEnum.Security} />
                </Route>
                <Route path={Routes.PrivacyPolicy} exact={true}>
                  <Markdown messageId={MarkDownEnum.PrivacyPolicy} />
                </Route>
                <Route path={Routes.PromotionDetails}>
                  <PromotionDetailsPage />
                </Route>
                <Route path={Routes.PriceMatch}>
                  <Markdown messageId={MarkDownEnum.PriceMatch} />
                </Route>
                <Route path={Routes.Faq}>
                  <Markdown messageId={MarkDownEnum.Faq} />
                </Route>
                <Route path="*">
                  <HomePage />
                </Route>
              </Switch>
            </React.Suspense>
            {!isAdminPage && <Footer />}
          </div>
        </IntlProvider>
      </ConfigProvider>
    );
  }
}

